import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import { jobRoleService } from '../services/jobManagementService';
import { useAuth } from '../contexts/AuthContext';

const JobApplicationFormPage = () => {
  const { jobRoleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobRole, setJobRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [formData, setFormData] = useState({
    jobPosting: '',
    destinationCountry: '',
    questionnaire: []
  });

  useEffect(() => {
    if (jobRoleId) {
      loadJobRole();
      checkExistingApplication();
    }
  }, [jobRoleId]);

  const loadJobRole = async () => {
    try {
      setLoading(true);
      const response = await jobRoleService.getById(jobRoleId);

      if (response.success) {
        const jobRoleData = response.data;
        setJobRole(jobRoleData);
        setFormData({
          jobPosting: jobRoleData.title,
          destinationCountry: jobRoleData.countryId?.name || '',
          questionnaire: [
            { question: 'Why are you interested in this position?', answer: '' },
            { question: 'What relevant experience do you have?', answer: '' },
            { question: 'When are you available to start?', answer: '' }
          ]
        });
      }
    } catch (error) {
      toast.error('Failed to load job details');
      navigate('/candidate/applications');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingApplication = async () => {
    try {
      // Check if user already has an application for this job
      const response = await applicationService.getApplications({
        candidateId: user.candidateId
      });
      if (response.success) {
        const inactiveStages = ['COMPLETED', 'REJECTED', 'WITHDRAWN'];
        const existingApp = response.applications?.find(app =>
          app.jobPosting === jobRole?.title &&
          !inactiveStages.includes(app.currentStage)
        );
        if (existingApp) {
          setHasApplied(true);
        }
      }
    } catch (error) {
      console.log('Could not check existing applications');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuestionnaireChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      questionnaire: prev.questionnaire.map((item, i) =>
        i === index ? { ...item, answer: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      // Create application using legacy system
      const applicationData = {
        candidateId: user.candidateId, // This should be set in the auth context
        jobPosting: formData.jobPosting,
        destinationCountry: formData.destinationCountry,
        questionnaire: formData.questionnaire.filter(q => q.answer.trim())
      };

      console.log('🚀 Submitting legacy application:', applicationData);

      const response = await applicationService.createApplication(applicationData);

      if (response.success) {
        toast.success('Application submitted successfully!');
        navigate('/candidate/applications');
      }
    } catch (error) {
      console.error('❌ Submission error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit application';
      toast.error(`Submission failed: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Check if user is candidate
  if (user?.role !== 'CANDIDATE') {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to Candidates.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading application form...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (hasApplied) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="text-yellow-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Already Applied</h2>
            <p className="text-gray-600 mb-4">You have already submitted an application for this job role.</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/candidate/applications')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                View My Applications
              </button>
              <button
                onClick={() => navigate('/candidate/applications')}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
              >
                Browse Other Jobs
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!jobRole) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="text-red-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Job Role Not Found</h2>
            <p className="text-gray-600 mb-4">The job role you're looking for is not available.</p>
            <button
              onClick={() => navigate('/candidate/applications')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Job Listings
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-8">
          <button
            onClick={() => navigate('/candidate/applications')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Applications
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Apply for {jobRole.title}</h1>
          <p className="text-gray-600">Country: {jobRole.countryId?.name}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} onKeyDown={(e) => {
            // Prevent form submission on Enter key except on submit button
            if (e.key === 'Enter' && e.target.type !== 'submit' && e.target.tagName !== 'BUTTON') {
              e.preventDefault();
            }
          }} className="space-y-6">
            {/* Job Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Position
              </label>
              <input
                type="text"
                value={formData.jobPosting}
                onChange={(e) => handleInputChange('jobPosting', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination Country
              </label>
              <input
                type="text"
                value={formData.destinationCountry}
                onChange={(e) => handleInputChange('destinationCountry', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                readOnly
              />
            </div>

            {/* Questionnaire */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Questions</h3>
              {formData.questionnaire.map((item, index) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {item.question}
                  </label>
                  <textarea
                    value={item.answer}
                    onChange={(e) => handleQuestionnaireChange(index, e.target.value)}
                    onKeyDown={(e) => {
                      // Prevent form submission on Enter key in textarea
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.stopPropagation();
                      }
                    }}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your answer..."
                  />
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/candidate/applications')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default JobApplicationFormPage;
